import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatIDR, parseIDR, formatDate, todayISO } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Modal, Badge, Textarea, Select } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { useTableFilter } from "@/hooks/useTableFilter";
import { useAuth } from "@/contexts/AuthContext";
import MasterCombo from "@/components/MasterCombo";
import { Plus, Trash2, X, Upload, FileText, Eye, ListChecks, Pencil, Wallet, Users } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import VendorManagerModal from "@/components/VendorManagerModal";

const blank = {
  date: todayISO(), vendor: "", vendor_id: "",
  items: [{ description: "", quantity: "", unit_price: "", amount: 0, category: "general" }],
  paid: false, notes: "",
  file_path: null, file_id: null, file_name: null, file_content_type: null,
  due_date: "",
  total_bill: "", paid_amount: 0,  // Iter v34ax — vendor payment tracking
};

// Iter v34b5 — Payment record blank state (mirror BillingList blankPay)
const blankPay = {
  amount: 0,
  payment_date: todayISO(),
  method: "transfer",
  note: "",
  proofs: [],
};

export default function Expenses() {
  const { t } = useLang();
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");  // Iter v34ax
  const [categories, setCategories] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const [dueAlert, setDueAlert] = useState({ count: 0, overdue_count: 0, rows: [] });
  // Iter v34bf — Master Vendors untuk dropdown pemilih vendor pada Expenses.
  const [vendorsList, setVendorsList] = useState([]);
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", phone: "", email: "" });
  // Iter v34ct (#2) — Modal kelola master vendor (tambah/edit/hapus).
  const [vendorMgrOpen, setVendorMgrOpen] = useState(false);
  const canManageVendors = hasPerm("vendors.manage") || hasPerm("expense.vendors_manage");
  // Iter v34b5 — Riwayat + record/edit payment (mirror BillingList)
  const [active, setActive] = useState(null);        // expense yg sedang dibuka
  const [payOpen, setPayOpen] = useState(false);     // modal record/edit payment
  const [pay, setPay] = useState(blankPay);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);  // modal riwayat
  const [payUploading, setPayUploading] = useState(false);
  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["vendor", "date", "items.description", "items.category"], "Cari pengeluaran…");
  const filtered = textFiltered
    .filter((r) => !categoryFilter || (r.items || []).some((it) => (it.category || "") === categoryFilter))
    .filter((r) => !vendorFilter || (r.vendor || "").toLowerCase().includes(vendorFilter.toLowerCase()));
  const uniqueVendors = Array.from(new Set(rows.map((r) => r.vendor).filter(Boolean))).sort();

  const load = () => Promise.all([
    api.get("/expenses"),
    api.get("/masters/categories"),
    api.get("/expenses/upcoming-due", { params: { window_days: 3 } }).catch(() => ({ data: { count: 0, overdue_count: 0, rows: [] } })),
    api.get("/vendors", { params: { active_only: true } }).catch(() => ({ data: [] })),
  ]).then(([e, c, due, vd]) => { setRows(e.data); setCategories(c.data); setDueAlert(due.data); setVendorsList(vd.data || []); });
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: "", unit_price: "", amount: 0, category: "general" }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  // Iter v34cq (#4) — Qty × Harga Satuan otomatis mengisi kolom Jumlah (Rp).
  const setItem = (idx, k, v) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it));
    if (k === "quantity" || k === "unit_price") {
      const q = parseIDR(items[idx].quantity);
      const up = parseIDR(items[idx].unit_price);
      if (String(items[idx].quantity ?? "").trim() !== "" && String(items[idx].unit_price ?? "").trim() !== "") {
        items[idx].amount = Math.round(q * up * 100) / 100;
      }
    }
    setForm({ ...form, items });
  };

  const submit = async () => {
    try {
      const items = form.items.map((i) => ({
        ...i,
        amount: parseIDR(i.amount),
        quantity: String(i.quantity ?? "").trim() === "" ? null : parseIDR(i.quantity),
        unit_price: String(i.unit_price ?? "").trim() === "" ? null : parseIDR(i.unit_price),
      }));
      const auto_total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      await api.post("/expenses", {
        ...form,
        items,
        total_bill: form.total_bill ? parseIDR(form.total_bill) : auto_total,
        paid_amount: parseIDR(form.paid_amount) || 0,
      });
      toast.success("Pengeluaran tersimpan"); setOpen(false); setForm(blank); load();
    } catch (e) { toast.error("Gagal"); }
  };

  const togglePaid = async (id) => { await api.post(`/expenses/${id}/toggle-paid`); load(); };
  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/expenses/${id}`); load(); };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "expense");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, file_path: r.data.path, file_id: r.data.id, file_name: r.data.filename, file_content_type: r.data.content_type });
      toast.success("Bukti terunggah");
    } catch (err) { toast.error("Gagal upload"); }
    finally { setUploading(false); }
  };

  // Iter v34b5 — Record / Edit payment handlers (mirror BillingList)
  const openNewPayment = (exp) => {
    setActive(exp);
    const remaining = Number(exp.remaining ?? ((exp.total_bill || exp.total || 0) - (exp.paid_amount || 0)));
    setPay({ ...blankPay, amount: remaining > 0 ? remaining : "" });
    setEditingPaymentId(null);
    setPayOpen(true);
  };

  const openEditPayment = (p) => {
    setEditingPaymentId(p.id);
    const proofs = Array.isArray(p.proofs) && p.proofs.length > 0
      ? p.proofs.map((x) => ({ id: x.id, path: x.path, filename: x.filename, content_type: x.content_type }))
      : (p.proof_id ? [{ id: p.proof_id, path: p.proof_path, filename: p.proof_filename, content_type: p.proof_content_type }] : []);
    setPay({
      amount: p.amount,
      payment_date: p.date,
      method: p.method || "transfer",
      note: p.note || "",
      proofs,
    });
    setPayOpen(true);
  };

  const uploadPayProof = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPayUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "expense_payment");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: r.data.id, path: r.data.path, filename: r.data.filename, content_type: r.data.content_type });
      }
      setPay((p) => ({ ...p, proofs: [...(p.proofs || []), ...uploaded] }));
      toast.success(files.length > 1 ? `${files.length} bukti terunggah` : "Bukti terunggah");
      e.target.value = "";
    } catch (err) { toast.error("Gagal upload"); }
    finally { setPayUploading(false); }
  };

  const removePayProof = (idx) => {
    setPay((p) => ({ ...p, proofs: (p.proofs || []).filter((_, i) => i !== idx) }));
  };

  const submitPayment = async () => {
    if (!active) return;
    try {
      const proofs = pay.proofs || [];
      const first = proofs[0] || {};
      const payload = {
        amount: parseIDR(pay.amount),
        payment_date: pay.payment_date,
        method: pay.method,
        note: pay.note,
        proofs,
        proof_id: first.id || "",
        proof_path: first.path || "",
        proof_filename: first.filename || "",
        proof_content_type: first.content_type || "",
      };
      if (!payload.amount) { toast.error("Nominal wajib diisi (boleh negatif untuk koreksi)"); return; }
      if (editingPaymentId) {
        await api.patch(`/expenses/${active.id}/payment/${editingPaymentId}`, payload);
        toast.success("Pembayaran diperbarui");
      } else {
        await api.post(`/expenses/${active.id}/payment`, payload);
        toast.success("Pembayaran tercatat");
      }
      setPayOpen(false);
      setEditingPaymentId(null);
      const updated = await api.get(`/expenses/${active.id}`);
      setActive(updated.data);
      load();
    } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || "")); }
  };

  const delPayment = async (paymentId) => {
    if (!window.confirm("Hapus pembayaran ini?")) return;
    try {
      await api.delete(`/expenses/${active.id}/payment/${paymentId}`);
      const updated = await api.get(`/expenses/${active.id}`);
      setActive(updated.data);
      load();
      toast.success("Pembayaran dihapus");
    } catch (e) { toast.error("Gagal"); }
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Keuangan · Pengeluaran"
        title={t("expense_recap")}
        description="Catat pengeluaran (multi-item per nota), unggah bukti, dan tandai lunas/belum lunas. Sinkron dengan dashboard."
        action={hasPerm("expense.create") && <Button onClick={() => { setForm(blank); setOpen(true); }} data-testid="add-expense-btn"><Plus className="h-4 w-4" />{t("expenses")}</Button>}
      />

      {/* Bento KPI + category breakdown */}
      {(() => {
        const filteredForStats = categoryFilter ? rows.filter((r) => (r.category || "") === categoryFilter) : rows;
        const totalAmount = filteredForStats.reduce((s, r) => s + (Number(r.total_bill || r.total) || 0), 0);
        // Iter v34b4 (fix #3) — Hitung berdasarkan `paid_amount` aktual, bukan flag status yg tidak ada.
        // Mendukung partial payment: paidAmount = sum(paid_amount), unpaid = total_bill - paid_amount.
        const paidAmount = filteredForStats.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
        const unpaidAmount = Math.max(0, totalAmount - paidAmount);
        const byCat = {};
        for (const r of filteredForStats) {
          const c = r.category || "lain-lain";
          byCat[c] = (byCat[c] || 0) + (Number(r.total) || 0);
        }
        const topCats = Object.entries(byCat).sort((a, b) => b[1] - a[1]).slice(0, 4);
        const catColors = ["from-blue-500 to-emerald-400", "from-emerald-500 to-teal-400", "from-amber-500 to-orange-400", "from-violet-500 to-fuchsia-400"];
        return (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4 rounded-2xl bg-gradient-to-br from-stone-900 via-stone-800 to-red-900 text-white p-6 relative overflow-hidden">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-red-500/20 blur-3xl"></div>
              <div className="relative">
                <div className="text-[11px] uppercase tracking-widest text-stone-400">Total Pengeluaran</div>
                <div className="font-display text-4xl mt-2 font-mono-num">{formatIDR(totalAmount)}</div>
                <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-300">✓ Lunas</span>
                    <span className="font-mono-num text-emerald-300">{formatIDR(paidAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-amber-300">⧗ Belum Lunas</span>
                    <span className="font-mono-num text-amber-300">{formatIDR(unpaidAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 rounded-2xl bg-white border border-stone-200/70 p-5">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-3">Top Kategori Pengeluaran</div>
              {topCats.length === 0 ? (
                <p className="text-sm text-stone-400 py-4">Belum ada data pengeluaran.</p>
              ) : (
                <div className="space-y-2.5">
                  {topCats.map(([cat, amt], i) => {
                    const pct = totalAmount > 0 ? (amt / totalAmount) * 100 : 0;
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="capitalize font-medium text-stone-800">{cat}</span>
                          <span className="font-mono-num text-stone-700">{formatIDR(amt)} <span className="text-stone-400">({pct.toFixed(1)}%)</span></span>
                        </div>
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${catColors[i % catColors.length]} rounded-full`} style={{ width: pct + "%" }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {FilterBar}
        <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="!w-auto" data-testid="exp-category-filter">
          <option value="">Semua Kategori</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </Select>
        <Select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} className="!w-auto" data-testid="exp-vendor-filter">
          <option value="">Semua Vendor</option>
          {uniqueVendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
        {/* Iter v34ct (#2) — Kelola master vendor langsung dari Rekap Pengeluaran. */}
        {canManageVendors && (
          <Button variant="secondary" onClick={() => setVendorMgrOpen(true)} data-testid="exp-manage-vendors-btn">
            <Users className="h-3.5 w-3.5" /> Kelola Vendor
          </Button>
        )}
      </div>

      {/* Iter v34o (#4) — Banner notifikasi jatuh tempo (H-3 / overdue) untuk admin Finance */}
      {dueAlert.count > 0 && (
        <Card
          className={`p-4 border-l-4 ${dueAlert.overdue_count > 0 ? "border-l-red-500 bg-red-50/40" : "border-l-amber-400 bg-amber-50/40"}`}
          data-testid="exp-due-banner"
        >
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 ${dueAlert.overdue_count > 0 ? "text-red-600" : "text-amber-600"}`}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-semibold text-sm ${dueAlert.overdue_count > 0 ? "text-red-800" : "text-amber-800"}`}>
                {dueAlert.overdue_count > 0
                  ? `${dueAlert.overdue_count} pengeluaran SUDAH lewat jatuh tempo`
                  : `${dueAlert.count} pengeluaran akan jatuh tempo dalam 3 hari`}
              </div>
              <ul className="mt-2 space-y-1 text-xs text-stone-700 max-h-40 overflow-y-auto">
                {dueAlert.rows.slice(0, 10).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 border-b border-stone-200/50 py-1 last:border-0" data-testid={`exp-due-row-${r.id}`}>
                    <span className="font-mono text-[10px] text-stone-500">{formatDate(r.due_date)}</span>
                    <span className="flex-1 truncate font-medium">{r.vendor || "(tanpa vendor)"} — {formatIDR(r.total)}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 ${r.is_overdue ? "bg-red-200 text-red-900" : "bg-amber-200 text-amber-900"}`}>
                      {r.is_overdue ? `Telat ${Math.abs(r.days_to_due)}h` : r.days_to_due === 0 ? "Hari Ini" : `H-${r.days_to_due}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}


      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">Tanggal</th>
              <th className="py-3 px-3">{t("vendor")}</th>
              <th className="py-3 px-3">Items</th>
              <th className="py-3 px-3 text-right">Total Tagihan</th>
              <th className="py-3 px-3 text-right">Sudah Dibayar</th>
              <th className="py-3 px-3 text-right">Sisa</th>
              <th className="py-3 px-3">Jatuh Tempo</th>
              <th className="py-3 px-3">Status</th>
              {/* Iter v34bh — Kolom Bukti standalone dihapus (dipindah ke modal Riwayat Pembayaran). */}
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-stone-400">Tidak ada pengeluaran.</td></tr>}
            {filtered.map((r, idx) => {
              // Iter v34o (#4) — compute days_to_due locally for badge
              let dueBadge = null;
              if (r.due_date && !r.paid) {
                try {
                  const td = new Date(); td.setHours(0,0,0,0);
                  const dd = new Date(r.due_date); dd.setHours(0,0,0,0);
                  const diff = Math.round((dd - td) / 86400000);
                  if (diff < 0) dueBadge = <Badge tone="danger">Telat {Math.abs(diff)}h</Badge>;
                  else if (diff === 0) dueBadge = <Badge tone="warn">Hari Ini</Badge>;
                  else if (diff <= 3) dueBadge = <Badge tone="warn">H-{diff}</Badge>;
                } catch (_e) { /* ignore parse errors */ }
              }
              return (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`exp-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono text-stone-600">{formatDate(r.date)}</td>
                <td className="py-2.5 px-3 font-medium">{r.vendor || "—"}</td>
                <td className="py-2.5 px-3 text-stone-600">{(r.items || []).map((i) => i.description).join(", ").slice(0, 80)}</td>
                <td className="py-2.5 px-3 text-right font-mono-num">{formatIDR(Number(r.total_bill || r.total || 0))}</td>
                <td className="py-2.5 px-3 text-right font-mono-num text-emerald-700" data-testid={`exp-paid-${idx}`}>{formatIDR(Number(r.paid_amount || 0))}</td>
                <td className="py-2.5 px-3 text-right font-mono-num" data-testid={`exp-remaining-${idx}`}>
                  {(() => {
                    const rem = Number(r.remaining ?? ((r.total_bill || r.total || 0) - (r.paid_amount || 0)));
                    if (rem > 0) return <span className="text-amber-700">{formatIDR(rem)}</span>;
                    if (rem < 0) return <span className="text-blue-700">{formatIDR(rem)} <span className="text-[9px] uppercase">lebih</span></span>;
                    return <span className="text-stone-400">Rp 0</span>;
                  })()}
                </td>
                <td className="py-2.5 px-3">
                  {r.due_date ? (
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-[11px] text-stone-700">{formatDate(r.due_date)}</span>
                      {dueBadge}
                    </div>
                  ) : <span className="text-stone-300">—</span>}
                </td>
                <td className="py-2.5 px-3">
                  {/* Iter v34bh — Status LUNAS readonly (tidak dapat diklik). */}
                  <span data-testid={`exp-status-${idx}`}>
                    {r.paid ? <Badge tone="success">{t("paid")}</Badge> : <Badge tone="warn">{t("unpaid")}</Badge>}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right">
                  {/* Iter v34bh — Kolom "Bukti" standalone dihapus. Bukti pembayaran ada di dalam modal Riwayat Pembayaran (per pembayaran). */}
                  {hasPerm("expense.edit") && (
                    <button
                      onClick={() => { setActive(r); setPaymentsOpen(true); }}
                      className="text-stone-600 hover:text-terra-600 px-2 inline-flex items-center"
                      title="Riwayat Pembayaran & Bukti"
                      data-testid={`exp-payments-list-${idx}`}
                    >
                      <ListChecks className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {hasPerm("expense.edit") && Number(r.remaining ?? ((r.total_bill || r.total || 0) - (r.paid_amount || 0))) !== 0 && (
                    <button
                      onClick={() => openNewPayment(r)}
                      className="text-emerald-700 hover:text-emerald-800 px-2 inline-flex items-center"
                      title="Catat Pembayaran"
                      data-testid={`exp-record-pay-${idx}`}
                    >
                      <Wallet className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {hasPerm("expense.delete") && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus"><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={t("expenses")} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tanggal"><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label={t("vendor")}>
              {/* Iter v34bf — Pilih dari master vendor + fallback tambah baru */}
              <div className="flex gap-1">
                <Select
                  value={form.vendor_id || ""}
                  onChange={(e) => {
                    const vid = e.target.value;
                    const v = vendorsList.find((x) => x.id === vid);
                    setForm({ ...form, vendor_id: vid, vendor: v ? v.name : "" });
                  }}
                  data-testid="exp-vendor-select"
                >
                  <option value="">— Pilih Vendor —</option>
                  {vendorsList.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  {/* Fallback kompatibilitas: expense lama pakai nama free-text */}
                  {form.vendor && !form.vendor_id && <option value="">{form.vendor} (lama, tidak di master)</option>}
                </Select>
                {hasPerm("vendors.manage") && (
                  <button
                    type="button"
                    onClick={() => setNewVendorOpen(true)}
                    className="px-2 border border-stone-300 hover:bg-terra-50 hover:border-terra-300 text-stone-600 hover:text-terra-700"
                    title="Tambah vendor baru"
                    data-testid="exp-vendor-new-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
            </Field>
            <Field label="Jatuh Tempo (opsional)"><DatePicker value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="exp-due-date" /></Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">Item Pengeluaran</label>
              <Button variant="secondary" onClick={addItem} className="!py-1 !px-2 text-xs"><Plus className="h-3 w-3" />{t("add")}</Button>
            </div>
            <div className="border border-stone-200 divide-y divide-stone-100">
              <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-stone-50 text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                <div className="col-span-4">Deskripsi</div>
                <div className="col-span-2">Kategori</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Harga Satuan</div>
                <div className="col-span-2 text-right">Jumlah (Rp)</div>
                <div className="col-span-1"></div>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 p-2 items-center">
                  <Input className="col-span-4" placeholder="Deskripsi" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`exp-item-desc-${i}`} />
                  <div className="col-span-2"><MasterCombo source="categories" value={it.category} onChange={(v) => setItem(i, "category", v)} placeholder="Kategori" data-testid={`exp-item-cat-${i}`} /></div>
                  <Input className="col-span-1 font-mono-num" placeholder="Qty" value={it.quantity ?? ""} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`exp-item-qty-${i}`} />
                  <Input className="col-span-2 font-mono-num" placeholder="Harga sat." value={it.unit_price ?? ""} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`exp-item-unit-price-${i}`} />
                  <Input className="col-span-2 font-mono-num" placeholder="Rp" value={it.amount} onChange={(e) => setItem(i, "amount", e.target.value)} data-testid={`exp-item-amount-${i}`} />
                  <button onClick={() => rmItem(i)} className="col-span-1 text-stone-400 hover:text-red-700"><X className="h-4 w-4 mx-auto" /></button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-stone-500 mt-1">Isi Qty &amp; Harga Satuan → kolom Jumlah terhitung otomatis. Boleh juga isi Jumlah langsung.</p>
          </div>
          <Field label={t("upload_file") + " (Bukti, opsional)"}>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : (form.file_id ? "Ganti file" : "Pilih file")}
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={uploadFile} className="hidden" data-testid="exp-file-input" />
              </label>
              {form.file_id && (
                <>
                  <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><FileText className="h-3 w-3" />{form.file_name || "Terunggah"}</span>
                  <button type="button" onClick={() => setPreviewId({ id: form.file_id, name: form.file_name, ct: form.file_content_type })} className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 text-xs">
                    <Eye className="h-3.5 w-3.5" />Preview
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, file_path: null, file_id: null, file_name: null, file_content_type: null })} className="text-xs text-stone-500 hover:text-red-700">Hapus</button>
                </>
              )}
            </div>
          </Field>
          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Total Tagihan" hint="Kosongkan → auto = sum items">
              <Input value={form.total_bill} onChange={(e) => setForm({ ...form, total_bill: e.target.value })} placeholder="0 (auto)" className="font-mono-num text-right" data-testid="exp-total-bill" />
            </Field>
            <Field label="Sudah Dibayar (di muka)" hint="Bisa 0. Nanti bisa update via tombol Bayar">
              <Input value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} placeholder="0" className="font-mono-num text-right" data-testid="exp-paid-amount" />
            </Field>
            <Field label="Sisa">
              <Input readOnly value={(() => {
                const tb = parseIDR(form.total_bill) || form.items.reduce((s, it) => s + (parseIDR(it.amount) || 0), 0);
                const pa = parseIDR(form.paid_amount) || 0;
                return formatIDR(tb - pa);
              })()} className="font-mono-num text-right bg-white" data-testid="exp-remaining-preview" />
            </Field>
          </div>
          <Field label={t("notes")}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          {/* Iter v34bh — Checkbox "Sudah Lunas" DIHAPUS. Status lunas otomatis berdasarkan paid_amount vs total_bill (dihitung backend). */}
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-expense-btn">{t("save")}</Button>
          </div>
        </div>
      </Modal>
      <FilePreview open={!!previewId} onClose={() => setPreviewId(null)} fileId={previewId?.id} filename={previewId?.name} contentType={previewId?.ct} />

      {/* Iter v34b5 — Record/Edit Payment Modal (mirror BillingList) */}
      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={editingPaymentId ? "Edit Pembayaran" : "Rekam Pembayaran"} size="md">
        {active && (
          <div className="space-y-4" data-testid="expense-payment-modal">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm">
              <p className="font-medium">{active.vendor || "(tanpa vendor)"}</p>
              <p className="text-xs text-stone-500">{formatDate(active.date)}</p>
              <p className="font-mono-num mt-1">Sisa: {formatIDR((active.total_bill || active.total || 0) - (active.paid_amount || 0))}</p>
            </div>
            <Field label="Nominal Pembayaran" hint="Boleh negatif untuk koreksi/refund">
              <Input className="font-mono-num text-right" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="exp-pay-amount" autoFocus />
            </Field>
            <Field label="Tanggal Pembayaran">
              <DatePicker value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} data-testid="exp-pay-date" />
            </Field>
            <Field label="Metode">
              <Select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="exp-pay-method">
                <option value="transfer">Transfer</option>
                <option value="cash">Cash</option>
                <option value="check">Cek</option>
                <option value="manual">Manual</option>
              </Select>
            </Field>
            <Field label="Catatan">
              <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="exp-pay-note" />
            </Field>
            <Field label="Bukti Pembayaran (JPG/PNG/PDF · Bisa upload lebih dari satu)">
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm w-fit">
                  <Upload className="h-4 w-4" />{payUploading ? "Mengunggah…" : (pay.proofs?.length ? "Tambah bukti lagi" : "Pilih file (multi)")}
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={uploadPayProof} className="hidden" data-testid="exp-pay-proof-input" />
                </label>
                {(pay.proofs || []).length > 0 && (
                  <div className="space-y-1.5 border border-stone-200 rounded-sm p-2 bg-stone-50" data-testid="exp-pay-proof-list">
                    {(pay.proofs || []).map((pf, idx) => (
                      <div key={pf.id || idx} className="flex items-center gap-2 text-xs" data-testid={`exp-pay-proof-item-${idx}`}>
                        <FileText className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                        <span className="text-emerald-800 truncate flex-1">{pf.filename || "(tanpa nama)"}</span>
                        <button type="button" onClick={() => setPreviewId({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                          className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 shrink-0"
                          data-testid={`exp-pay-proof-preview-${idx}`}>
                          <Eye className="h-3.5 w-3.5" />Preview
                        </button>
                        <button type="button" onClick={() => removePayProof(idx)}
                          className="text-stone-500 hover:text-red-700 shrink-0"
                          data-testid={`exp-pay-proof-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Field>
            <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
              <Button variant="ghost" onClick={() => setPayOpen(false)}>Batal</Button>
              <Button onClick={submitPayment} data-testid="exp-pay-save-btn">Simpan</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v34b5 — Payment History Modal (mirror BillingList) */}
      <Modal open={paymentsOpen} onClose={() => setPaymentsOpen(false)} title="Riwayat Pembayaran" size="lg">
        {active && (
          <div className="space-y-4">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm flex items-center justify-between">
              <div>
                <p className="font-medium">{active.vendor || "(tanpa vendor)"}</p>
                <p className="text-xs text-stone-500">{formatDate(active.date)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-500">Total Tagihan</p>
                <p className="font-mono-num text-lg">{formatIDR(active.total_bill || active.total || 0)}</p>
                <p className="text-xs text-emerald-700">Dibayar: <span className="font-mono-num">{formatIDR(active.paid_amount || 0)}</span></p>
                <p className="text-xs text-amber-700">Sisa: <span className="font-mono-num">{formatIDR((active.total_bill || active.total || 0) - (active.paid_amount || 0))}</span></p>
              </div>
            </div>

            <table className="w-full text-sm border border-stone-200">
              <thead><tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                <th className="py-2 px-3">Tanggal</th><th className="py-2 px-3 text-right">Jumlah</th>
                <th className="py-2 px-3">Metode</th><th className="py-2 px-3">Catatan</th>
                <th className="py-2 px-3">Bukti</th><th className="py-2 px-3 text-right">Aksi</th>
              </tr></thead>
              <tbody>
                {(active.payments || []).length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-stone-400 text-xs">Belum ada pembayaran.</td></tr>
                )}
                {(active.payments || []).map((p, i) => (
                  <tr key={p.id || i} className="border-t border-stone-100" data-testid={`exp-payment-row-${i}`}>
                    <td className="py-2 px-3 font-mono text-xs">{formatDate(p.date)}</td>
                    <td className="py-2 px-3 text-right font-mono-num">{formatIDR(p.amount)}</td>
                    <td className="py-2 px-3 capitalize">{p.method || "—"}</td>
                    <td className="py-2 px-3 text-xs text-stone-600">{p.note || "—"}</td>
                    <td className="py-2 px-3">{(() => {
                      const proofs = (Array.isArray(p.proofs) && p.proofs.length > 0)
                        ? p.proofs
                        : (p.proof_id ? [{ id: p.proof_id, filename: p.proof_filename, content_type: p.proof_content_type }] : []);
                      if (proofs.length === 0) return <span className="text-stone-300">—</span>;
                      return (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid={`exp-proof-list-${i}`}>
                          {proofs.map((pf, pi) => (
                            <button key={pf.id || pi} onClick={() => setPreviewId({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                              className="inline-flex items-center gap-1 text-terra-600 hover:text-terra-700 hover:bg-terra-50 border border-stone-200 px-1.5 py-0.5 text-[11px]"
                              title={pf.filename}
                              data-testid={`exp-proof-preview-${i}-${pi}`}>
                              <Eye className="h-3 w-3" /><span>{pi + 1}</span>
                            </button>
                          ))}
                          {proofs.length > 1 && (
                            <span className="text-[10px] text-stone-500">{proofs.length} file</span>
                          )}
                        </div>
                      );
                    })()}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {p.id && hasPerm("expense.edit") && <button onClick={() => { setPaymentsOpen(false); openEditPayment(p); }} className="text-stone-500 hover:text-terra-600 px-2" data-testid={`exp-edit-payment-${i}`} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>}
                      {p.id && hasPerm("expense.edit") && <button onClick={() => delPayment(p.id)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`exp-del-payment-${i}`} title="Hapus"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-between pt-4 border-t border-stone-200">
              <Button variant="ghost" onClick={() => setPaymentsOpen(false)}>Tutup</Button>
              {hasPerm("expense.edit") && Number((active.total_bill || active.total || 0) - (active.paid_amount || 0)) > 0 && (
                <Button onClick={() => { setPaymentsOpen(false); openNewPayment(active); }} data-testid="exp-new-payment-btn">
                  <Plus className="h-4 w-4" />Tambah Pembayaran
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v34bf — Modal quick-create vendor baru */}
      <Modal open={newVendorOpen} onClose={() => setNewVendorOpen(false)} title="Tambah Vendor Baru">
        <div className="space-y-3">
          <Field label="Nama Vendor *"><Input value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })} placeholder="PT Supplier XYZ" data-testid="exp-new-vendor-name" autoFocus/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="No. Telepon"><Input value={newVendor.phone} onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })} placeholder="+62…"/></Field>
            <Field label="Email"><Input value={newVendor.email} onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })} placeholder="vendor@…"/></Field>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setNewVendorOpen(false)}>Batal</Button>
            <Button
              onClick={async () => {
                if (!(newVendor.name || "").trim()) return toast.error("Nama vendor wajib diisi");
                try {
                  const r = await api.post("/vendors", { ...newVendor, is_active: true });
                  toast.success("Vendor ditambahkan");
                  const created = r.data;
                  // Auto-select vendor baru & refresh master list
                  setVendorsList((prev) => [...prev, created].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
                  setForm({ ...form, vendor_id: created.id, vendor: created.name });
                  setNewVendor({ name: "", phone: "", email: "" });
                  setNewVendorOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Gagal simpan vendor");
                }
              }}
              data-testid="exp-new-vendor-save"
            >
              <Plus className="h-4 w-4" />Simpan & Pilih
            </Button>
          </div>
        </div>
      </Modal>

      {/* Iter v34ct (#2) — Kelola Master Vendor */}
      <VendorManagerModal
        open={vendorMgrOpen}
        onClose={() => setVendorMgrOpen(false)}
        onChanged={() => api.get("/vendors", { params: { active_only: true } }).then((r) => setVendorsList(r.data || [])).catch(() => {})}
      />
    </div>
    </div>
  );
}
